//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import axios from '../../../../../_utils/axios';
import DateTimeDisplay from '../../../../_common/DateTimeDisplay';
import FileType from '../../../../_common/Icons/FileType';
import PolarisIcon from '../../../../_common/PolarisIcon';
import { useToast } from '../../../../_common/Toast';
import { RowColumns } from './DailyReportsWidget';
import { DailyReport } from '../../../../../redux/slices/slice_types';
import { formatBytes } from '@/_utils/utils';
import { UserDisplay } from '@/exports/columnCommonComponents';
import { useDownload } from '@/hooks/useDownload';

export const ReportFile = ({
  columns,
  report,
  isDownloaded,
  downloadCallback,
  includeCreatedBy = false,
  asyncView = false
}: {
  columns: RowColumns;
  report: DailyReport;
  isDownloaded: boolean;
  downloadCallback: () => void;
  includeCreatedBy?: boolean;
  asyncView?: boolean;
}) => {
  const { info, error } = useToast();
  const { download } = useDownload();
  const data: { [key: keyof RowColumns]: any } = {
    file_name: (
      <FileName>
        <FileType type={report.file_info.file_type} height={20} />
        <span>{report.name}</span>
      </FileName>
    ),
    created_by: (
      <AlignedCell align={columns.created_by.align}>
        <div style={{ paddingRight: '8px', maxWidth: '100%' }}>
          {includeCreatedBy ? report.created_by ? <UserDisplay user={report.created_by} emptyText="-" /> : '-' : null}
        </div>
      </AlignedCell>
    ),
    created_at: <DateTimeDisplay>{report.created_at}</DateTimeDisplay>,
    file_size: <AlignedCell align={columns.file_size.align}>{formatBytes(report.file_info.file_size)}</AlignedCell>,
    action: (
      <AlignedCell align={columns.action.align}>
        <PolarisIcon icon={isDownloaded ? 'cloud_check' : 'cloud_download'} height={20} />
      </AlignedCell>
    )
  };

  const handleDownload = () => {
    if (asyncView && report.file_info.file_size === 0)
      return error('The file is still being generated. Please try downloading again in a few moments.');
    return axios
      .get(`/reporting/${report.id}/`)
      .then((response) => {
        const url = response.data?.data?.download_url;
        if (!url) throw new Error();
        download({ url, fileName: report.name });
        info('Download request received.');
        downloadCallback();
      })
      .catch(() => error('There was a problem downloading the report.'));
  };

  return (
    <ReportEntry onClick={handleDownload} container>
      {Object.keys(columns).map((key) => {
        if (key === 'created_by' && !includeCreatedBy) return null;
        if (asyncView && key === 'action' && report.file_info.file_size === 0) return null; // Don't show action icon if file isn't ready
        if (asyncView && key === 'file_size' && report.file_info.file_size === 0) {
          return (
            <Grid key={key} item xs={columns[key].cols + columns.action.cols} sx={{ display: 'flex', alignItems: 'center' }}>
              <ThreeDots>Generating Report...</ThreeDots>
            </Grid>
          );
        }
        return (
          <Grid key={key} item xs={columns[key].cols}>
            {data[key]}
          </Grid>
        );
      })}
    </ReportEntry>
  );
};

const ReportEntry = styled(Grid)`
  background-color: ${({ theme }) => theme.palette.background.default};
  padding: ${({ theme }) => theme.spacing(1, 0.5)};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    svg:first-of-type {
      display: none;
    }
    svg:last-of-type {
      display: block;
    }
  }
`;

const FileName = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  margin-left: ${({ theme }) => theme.spacing(2)};

  span {
    margin-left: ${({ theme }) => theme.spacing(1)};
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const AlignedCell = styled.div<{ align: 'center' | 'flex-start' | 'flex-end' }>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ align }) => align || 'flex-start'};
  align-items: center;
  height: 100%;

  span {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const ThreeDots = styled.div`
  font-weight: bold;
  display: inline-block;
  clip-path: inset(0 1ch 0 0);
  animation: l 1s steps(4, jump-none) infinite;

  @keyframes l {
    to {
      clip-path: inset(0);
    }
  }
`;
